import { FC, memo, useEffect, useImperativeHandle, useRef } from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import gsap from 'gsap'

import css from './PagePhoto.module.scss'

import { PhotoCopyType } from '@/data/fixed-pages/copy-photo'
import { PageHandle, PageProps } from '@/data/types'

import { toRem, updateCustomProperties } from '@/utils/basic-functions'
import { MOTION } from '@/utils/motion-values'
import sass from '@/utils/sass'

import { useRefs } from '@/hooks/use-refs'

import Heading from '@/components/Heading/Heading'
import Label from '@/components/Label/Label'
import PhotoGrid from '@/components/PhotoGrid/PhotoGrid'
import PillButton from '@/components/PillButton/PillButton'
export interface PagePhotoProps extends PageProps {
  className?: string
  content: PhotoCopyType
}

export type ViewRefs = {
  root: HTMLDivElement
  heading: HTMLHeadingElement
  button: HTMLAnchorElement
  description: HTMLDivElement
  photoGrid: HTMLDivElement
  year: HTMLDivElement
}

const View: FC<PagePhotoProps> = ({ className, content, onReady }) => {
  const handleRef = useRef<PageHandle>(null)
  const viewRefs = useRefs<ViewRefs>()

  useEffect(() => {
    const photos = Array.from(viewRefs.photoGrid.current!.childNodes)

    gsap.set(viewRefs.root.current, { opacity: 0 })
    gsap.set(viewRefs.heading.current, { opacity: 0 })
    gsap.set(viewRefs.button.current, { opacity: 0, y: toRem(10) })
    gsap.set(photos, { opacity: 0, y: toRem(45) })

    setTimeout(() => {
      onReady?.(handleRef)
    }, MOTION.general.delayForLayout)
  }, [onReady, viewRefs])

  useImperativeHandle(handleRef, () => ({
    animateIn: () => {
      const photos = Array.from(viewRefs.photoGrid.current!.childNodes)
      const pillButton = viewRefs.button.current!
      const description = viewRefs.description.current!
      const year = viewRefs.year.current!

      // @NOTE: Reorder photos to match the motion bible
      const reorderPhotos = [photos[0], photos[2], photos[1], photos[5], photos[3], photos[4]].filter(Boolean)

      return gsap
        .timeline()
        .set(viewRefs.root.current, { opacity: 1 }, 0)
        .set(viewRefs.heading.current, { opacity: 1 }, 0)
        .textRiseByCharsIn(
          viewRefs.heading.current!,
          {
            duration: 1.5,
            charDuration: 1.5,
            charOffset: 0.075,
            ease: 'ease-split-text',
            immediateRender: true
          },
          '<+0.1'
        )
        .to(reorderPhotos, { opacity: 1, duration: 0.8, stagger: 0.1, ease: 'ease-opacity' }, '<+0.334')
        .to(reorderPhotos, { y: 0, duration: 1.2, stagger: 0.1, ease: 'ease-transform' }, '<')
        .to(pillButton, { opacity: 1, duration: 0.8, ease: 'ease-opacity' }, '<+0.2')
        .to(pillButton, { y: 10, duration: 1.2, ease: 'ease-transform' }, '<')
        .fromTo(description, { opacity: 0 }, { opacity: 1, duration: 0.8, ease: 'ease-opacity' }, '<')
        .fromTo(year, { opacity: 0 }, { opacity: 1, duration: 0.8, ease: 'ease-opacity' }, '<+0.5')
    },
    animateOut: () => {
      return gsap.timeline().to(viewRefs.root.current, { autoAlpha: 0, ease: 'ease-opacity', duration: 0.8 }, 0)
    }
  }))

  useEffect(() => {
    updateCustomProperties({
      backButtonVisibility: 'visible',
      backButtonColor: sass.black,
      backButtonOpacity: '1',
      navButtonBackground: sass.black,
      navButtonColor: sass.white,
      navButtonBorder: 'transparent'
    })
  }, [])

  return (
    <div className={classNames('PagePhoto', css.root, className)} ref={viewRefs.root}>
      <Heading as="h1" uppercase className={css.header} ref={viewRefs.heading}>
        {content.title}
      </Heading>

      <Link className={css.ctaButton} href={content.cta.link} scroll={false} ref={viewRefs.button}>
        <PillButton flexAlignment="start" theme="dark">
          {content.cta.label}
        </PillButton>
      </Link>

      <PhotoGrid className={css.photoGrid} images={content.images} ref={viewRefs.photoGrid} />
      <div className={css.bottomContainer}>
        <Label ref={viewRefs.description} labelType={3}>
          {content.labels.description}
        </Label>
        <Label ref={viewRefs.year} labelType={3}>
          {content.labels.year}
        </Label>
      </div>
    </div>
  )
}

View.displayName = 'PagePhoto-View'

// Controller (handles global state, router, data fetching, etc. Feeds props to the view component)
const PagePhoto: FC<PagePhotoProps> = (props) => {
  return <View {...props} />
}

PagePhoto.displayName = 'PageAbout'

export default memo(PagePhoto)
